import AddToCalendarButtonContainer from 'app/components/AddToCalendarButtonContainer/AddToCalendarButtonContainer';
import CartSteps from 'app/components/CartSteps';
import DownloadOrderInvoiceButton from 'app/components/DownloadOrderInvoiceButton/DownloadOrderInvoiceButton';
import NoIndexSEO from 'app/components/SEO/NoIndexSEO';
import OrderConfirmationPageContentSkeleton from 'app/components/Skeletons/OrderConfirmationPage/OrderConfirmationPageContentSkeleton';
import TicketsSectionOrderConfirmationPage from 'app/components/TicketsSectionOrderConfirmationPage/TicketsSectionOrderConfirmationPage';
import { Button } from 'app/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { formatDueDate } from 'utils/events';
import {
  STATUS_APPROVED,
  STATUS_COMPLETE,
  STATUS_PAID,
  STATUS_PENDING_PAYMENT,
} from 'utils/orderStatuses';
import { ORDER_CONFIRMATION_PAGE_TYPE_TEXT } from 'utils/static/constants';
import EventDetailsOnOrderConfirmationPage from './EventDetailsOnOrderConfirmationPage';
import StatusPendingAdditionalInformation from './StatusPendingAdditionalInformation';
import useOrderConfirmationPageData from './useOrderConfirmationPageData';

const OrderConfirmationPage: React.FC<{}> = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get('uuid');
  const { state } = useLocation();

  const {
    isLoading,
    orderData,
    eventData,
    eventData: {
      name,
      location,
      timeInterval,
      dateTimeFormat: { dateFormat, timeFormat, timezone },
      dateTimeFormat,
      checkoutConfiguration,
      registrationform,
    },
  } = useOrderConfirmationPageData(orderId);

  // useEmbedResize([isLoading, orderData.status]);

  const formattedPlacementDate = isLoading
    ? ''
    : formatDueDate(
        dateFormat,
        timeFormat,
        orderData.placedAt,
        orderData.createdAt,
        orderData.dueDays,
        t('common.at'),
      );

  const shouldDisplayTheOrderConfirmationText =
    checkoutConfiguration.orderConfirmationPageType ===
      ORDER_CONFIRMATION_PAGE_TYPE_TEXT &&
    checkoutConfiguration.orderConfirmationPageText &&
    (orderData.status === STATUS_COMPLETE ||
      orderData.status === STATUS_APPROVED);

  const eventHasRegistrationForms = registrationform.length > 0;

  let pageTitle = '';

  if (isLoading === false) {
    if (
      orderData.status === STATUS_COMPLETE ||
      orderData.status === STATUS_APPROVED
    ) {
      pageTitle = t('orderConfirmationPage.orderComplete');
    } else {
      pageTitle = t('orderConfirmationPage.orderConfirmed');
    }
  }

  return (
    <div className="page-container">
      <NoIndexSEO pageTitle={pageTitle} />
      <div className="section-container">
        {state?.eventHasRegistrationForms ? (
          <CartSteps
            eventHasRegistrationForms={state.eventHasRegistrationForms}
            displayAddonsStep={state.hasAddonsOrTopUps}
          />
        ) : (
          <CartSteps
            isLoading={isLoading}
            eventHasRegistrationForms={eventHasRegistrationForms}
            displayAddonsStep={orderData.hasAddonsOrTopUps}
          />
        )}
        <div className="cart-step-card">
          <div className="cart-step-card-content rounded-lg">
            {isLoading ? (
              <OrderConfirmationPageContentSkeleton
                displayCardFooter={state?.eventHasRegistrationForms}
              />
            ) : (
              <div className="w-full">
                <div className="border-b border-neutral-40 pb-[16px] sm:pb-[20px]">
                  <div className="flex justify-between items-center gap-[8px] flex-wrap">
                    <h2 className="title-md flex items-center gap-[8px]">
                      {orderData.status === STATUS_COMPLETE ||
                      orderData.status === STATUS_APPROVED
                        ? '✅ ' + t('orderConfirmationPage.orderComplete')
                        : '🕔 ' + t('orderConfirmationPage.orderConfirmed')}
                    </h2>
                    <div className="flex items-center gap-2">
                      {orderData.invoice ? (
                        <DownloadOrderInvoiceButton
                          downloadLink={orderData.invoice}
                        />
                      ) : null}
                      {timeInterval.startsAt ? (
                        <AddToCalendarButtonContainer
                          eventData={{
                            name,
                            location,
                            timeInterval,
                            timezone,
                          }}
                        />
                      ) : null}
                      {orderData.hasNFT ? (
                        <a
                          href={`${process.env.REACT_APP_OVEIT_DASHBOARD_LINK}/orders/thank_you_nft?id=${orderData.uuid}`}
                          target="_blank"
                        >
                          <button className="btn btn-blue flex gap-[8px] items-center h-[33px] sm:h-[36px]">
                            <p className="p2-medium">
                              {t('orderConfirmationPage.mintButtonText')}
                            </p>
                          </button>
                        </a>
                      ) : null}
                    </div>
                  </div>
                  {shouldDisplayTheOrderConfirmationText && (
                    <div
                      className="element-contains-html order-confirmation-text mt-4 p2-regular"
                      dangerouslySetInnerHTML={{
                        __html:
                          checkoutConfiguration.orderConfirmationPageText as
                            | string
                            | TrustedHTML,
                      }}
                    />
                  )}
                  {orderData.status === STATUS_PENDING_PAYMENT ||
                  (orderData.status === STATUS_PAID &&
                    orderData.pendingReason === 'vies') ? (
                    <div className="mt-[8px]">
                      <StatusPendingAdditionalInformation
                        status={orderData.status}
                        pendingReason={orderData.pendingReason}
                        dueDate={formattedPlacementDate}
                        displayTickets={checkoutConfiguration.displayTickets}
                      />
                    </div>
                  ) : null}
                </div>
                <div
                  className={`w-full ${
                    orderData.tickets?.length > 0
                      ? 'py-[16px] sm:py-[20px]'
                      : 'pt-[16px] sm:pt-[20px]'
                  }`}
                >
                  <EventDetailsOnOrderConfirmationPage
                    numberOfTickets={orderData.numberOfTickets}
                    event={eventData}
                  />
                </div>
                {(orderData.status === STATUS_COMPLETE ||
                  orderData.status === STATUS_APPROVED) &&
                orderData.tickets?.length > 0 &&
                checkoutConfiguration.displayTickets ? (
                  <div className="pt-[16px] sm:pt-[20px] border-t border-neutral-40">
                    <h2 className="title-md mb-[16px]">
                      {t('orderConfirmationPage.yourTickets')}
                    </h2>
                    <TicketsSectionOrderConfirmationPage
                      tickets={orderData.tickets}
                      registrationForms={eventData.registrationform}
                      dateTimeFormat={dateTimeFormat}
                      eventId={eventData.id}
                      hasSeatingChart={eventData.hasSeatingChart}
                      eventRegistrationForm={eventData.registrationform}
                    />
                  </div>
                ) : null}
              </div>
            )}
          </div>
          {eventHasRegistrationForms && (
            <div className="cart-step-card-footer">
              <Button variant="link" asChild>
                <Link
                  to={`/order/assign-tickets?uuid=${orderId}`}
                  className="flex items-center"
                  state={{
                    hasAddonsOrTopUps:
                      state?.hasAddonsOrTopUps || orderData.hasAddonsOrTopUps,
                  }}
                >
                  <ArrowLeft />
                  {t('common.back')}
                </Link>
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrderConfirmationPage;
